<script setup lang="ts">
import type {
	PropertyPhotoCarouselMobileEmits,
	PropertyPhotoCarouselMobileProps
} from '.';
import type { PropertyImage } from '~/core/entities/property';

import { EventBusKeys } from '~/core/helpers/symbols';
import { getPropertyImageAlt } from '~/utils';
import { screenSizesInPixels } from '~~/utils';

import { HlsVideoPlayer } from '~/components/HlsVideoPlayer';
import { StandardSlider } from '~/components/StandardSlider';
import { Skeleton } from '~/components/ui/skeleton';

interface CarouselItem {
	type: 'image' | 'video';
	image?: { watermarkUrl: string; url?: string };
	videoUrl?: string;
	posterUrl?: string;
	index?: number;
}

const props = withDefaults(defineProps<PropertyPhotoCarouselMobileProps>(), {
	isLoading: false,
	sectionName: 'hero section'
});

const emit = defineEmits<PropertyPhotoCarouselMobileEmits>();

const standardSliderRef = useTemplateRef<{
	currentCarouselItemIndex: number;
	scrollableSlidesNumber: number;
	scrollTo: (index: number, immediate?: boolean) => void;
	reinitializeCarousel: (() => void) | undefined;
}>('standardSliderRef');


const carouselItems = computed<CarouselItem[]>(() => {
	const items: CarouselItem[] = [];

	// If has video, show it as first item
	if (props.videoUrl) {
		items.push({
			type: 'video',
			videoUrl: props.videoUrl,
			posterUrl: props.posterUrl
		});
	}

	// Add all images
	props.images.forEach((image, index) => {
		items.push({
			type: 'image',
			image,
			index
		});
	});

	return items;
});
const breakpoints = useBreakpoints(screenSizesInPixels);

// SSR-safe: default to false on server, update on client
const isLaptop = ref(false);
onMounted(() => {
	isLaptop.value = breakpoints.between('lg', '2xl').value;

	watch(
		() => breakpoints.between('lg', '2xl').value,
		newValue => {
			isLaptop.value = newValue;
		}
	);
});

const currentPhotoCount = computed(() => {
	const currentIndex = standardSliderRef.value?.currentCarouselItemIndex ?? 0;
	const hasVideo = !!props.videoUrl;

	// If on video slide, return 0
	if (hasVideo && currentIndex === 0) {
		return 0;
	}

	// Adjust index if video is present (first item is video, not photo)
	const photoIndex = hasVideo ? currentIndex - 1 : currentIndex;

	if (isLaptop.value) {
		return photoIndex + 2;
	}

	return photoIndex + 1;
});

function getImageAlt(index: number | null) {
	return getPropertyImageAlt(
		props.property?.propertyType?.name,
		props.property?.suites,
		props.property?.region,
		props.property?.city,
		props.property?.askingPrice,
		index
	);
}

function handlePhotoClick(
	photoIndex: number,
	photoUrl: string,
	clickType: 'photo' | 'cta' = 'photo'
) {
	emit('photo-clicked', {
		photoIndex,
		photoUrl,
		clickType
	});
}

const mobileCarouselCountBus = useEventBus(
	EventBusKeys.mobileHeroSectionPhotoCount
);

watch(
	currentPhotoCount,
	newValue => {
		mobileCarouselCountBus.emit(newValue);
	},
	{ immediate: true }
);
</script>

<template>
	<div class="size-full xl:hidden">
		<component
			:is="StandardSlider<CarouselItem>"
			ref="standardSliderRef"
			:items="carouselItems"
			:is-loading="isLoading"
			:section-name="sectionName"
			carousel-item-size="half-seamless"
			:slides-to-scroll="1"
			:carousel-options="{
				cyclic: true,
				align: 'center',
				class: 'size-full',
				carouselWrapperClasses: 'h-full'
			}"
			:show-navigation-arrows="true"
			:always-show-arrows="true"
			:display-title="false"
			:display-footer="false"
			:limit-photos="false"
			title=""
			container-class="!p-0 h-full"
			main-container-class="h-full"
			class="h-full"
			carousel-content-class="!p-0 h-full !gap-1"
		>
			<template #items="{ item, index }">
				<div class="h-full w-full overflow-hidden bg-black">
					<ClientOnly v-if="item.type === 'video' && item.videoUrl">
						<HlsVideoPlayer
							:video-url="item.videoUrl"
							:poster-url="item.posterUrl"
							:autoplay="true"
							:muted="true"
							:controls="true"
							:loop="true"
							class="size-full"
						/>
						<template #fallback>
							<!-- Show poster thumbnail while component hydrates -->
							<div class="flex size-full items-center justify-center bg-black">
								<NuxtImg
									v-if="item.posterUrl"
									:src="item.posterUrl"
									alt="Video thumbnail"
									class="size-full object-cover border-0 outline-none"
									format="webp"
									quality="80"
									loading="eager"
									fetchpriority="high"
								/>
							</div>
						</template>
					</ClientOnly>

					<Skeleton
						v-if="item.type === 'image' && item.image"
						:is-active="isLoading"
						class="size-full"
						variant="white"
					>
						<NuxtImg
							:src="item.watermarkUrl"
							:alt="getImageAlt(index)"
							width="640"
							height="360"
							sizes="xs:375px sm:640px md:512px lg:512px"
							densities="x1"
							:preload="index === 0 ? { fetchPriority: 'high' } : undefined"
							:loading="index === 0 ? 'eager' : 'lazy'"
							decoding="async"
							class="aspect-video size-full object-cover"
							format="webp"
							quality="80"
							@click="
								handlePhotoClick(item.index ?? 0, item.image.watermarkUrl)
							"
						/>
					</Skeleton>
				</div>
			</template>

			<template #skeleton>
				<div class="size-full">
					<Skeleton class="size-full" variant="white" :is-active="true" />
				</div>
			</template>
		</component>
	</div>
</template>
